import { FC, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GridApi } from '@mui/x-data-grid';
import TableFiltersToolbarStatusPanel from './TableFiltersToolbarStatusPanel';

interface AppliedFiltersBarProps {
  apiRef: RefObject<GridApi>;
  onHeightChange: (height: number) => void;
}

// The bar can't live in the scroller (MUI computes header/row offsets analytically, which breaks
// virtualization + infinite scroll), so it is overlaid into the enlarged column-header row instead.
const AppliedFiltersBar: FC<AppliedFiltersBarProps> = ({ apiRef, onHeightChange }) => {
  const [headersEl, setHeadersEl] = useState<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver>();

  useEffect(() => {
    let rafId = 0;

    const findHeaders = () => {
      const el = apiRef.current?.rootElementRef?.current?.querySelector<HTMLDivElement>('.MuiDataGrid-columnHeaders');

      if (el) {
        setHeadersEl(el);
      } else {
        rafId = requestAnimationFrame(findHeaders);
      }
    };

    findHeaders();

    return () => cancelAnimationFrame(rafId);
  }, [apiRef]);

  // measure via callback ref so the ResizeObserver lives with the node instead of a separate effect
  const measureRef = useCallback(
    (el: HTMLDivElement | null) => {
      observerRef.current?.disconnect();

      if (!el) {
        onHeightChange(0);

        return;
      }

      observerRef.current = new ResizeObserver(() => onHeightChange(el.offsetHeight));
      observerRef.current.observe(el);
    },
    [onHeightChange],
  );

  if (!headersEl) {
    return null;
  }

  return createPortal(
    <div ref={measureRef} style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 10 }}>
      <TableFiltersToolbarStatusPanel />
    </div>,
    headersEl,
  );
};

export default AppliedFiltersBar;
